import { type ReactNode, useEffect, useRef } from 'react';
import { CheckCircle2, X, XCircle } from 'lucide-react';

export interface ToastState {
  id: number;
  type: 'success' | 'error';
  message: string;
}

/**
 * 功能：渲染页面标题、编号和右侧操作区。
 * 参数：title（标题）；description（说明）；eyebrow（技术标签）；actions（操作按钮）。
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
}: { title: string; description: string; eyebrow: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <div className="eyebrow"><span />{eyebrow}</div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

/**
 * 功能：渲染带焦点管理的模态对话框。
 * 参数：open（是否显示）；title（标题）；onClose（关闭回调）；children（内容）；size（宽度级别）。
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  size = 'md',
}: { open: boolean; title: string; onClose: () => void; children: ReactNode; size?: 'sm' | 'md' | 'lg' }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const handleKey = (event: KeyboardEvent): void => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); previous?.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={`modal modal-${size}`} role="dialog" aria-modal="true" aria-labelledby="modal-title" tabIndex={-1} ref={dialogRef}>
        <div className="modal-head">
          <h2 id="modal-title">{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="关闭对话框" title="关闭"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * 功能：显示操作成功或失败通知。
 * 参数：toasts（通知数组）；onDismiss（移除通知回调）。
 */
export function Toasts({ toasts, onDismiss }: { toasts: ToastState[]; onDismiss: (id: number) => void }) {
  return (
    <div className="toast-stack" aria-live="polite">
      {toasts.map((toast) => (
        <button key={toast.id} className={`toast toast-${toast.type}`} onClick={() => onDismiss(toast.id)}>
          {toast.type === 'success' ? <CheckCircle2 size={17} /> : <XCircle size={17} />}
          <span>{toast.message}</span>
          <X size={14} />
        </button>
      ))}
    </div>
  );
}

/**
 * 功能：把时间转换为简洁中文本地显示。
 * 参数：value（ISO 时间或空值）。
 */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '尚未执行';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
}

/**
 * 功能：把系统状态转换为中文标签。
 * 参数：status（后端状态字符串）。
 */
export function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    healthy: '健康', warning: '待复检', damaged: '已损坏', unknown: '未检查',
    success: '注册成功', failed: '失败', pending: '处理中', available: '可用', reserved: '租用中',
  };
  return labels[status] || status;
}
